import { CheckVerifiedSolid } from "@lazarentertainment/web-design-system/components/iconSolid"
import * as AllIcons from "@lazarentertainment/web-design-system/components/iconSolid"
import { StageTen } from "@lazarentertainment/web-design-system/components/logo"
import Linkify from "client/packages/chat/components/chat-message/Linkify"
import { ComponentPropsWithoutRef, forwardRef, ReactNode } from "react"

export type ChatNameProps = {
  /**
   * Adds a 'verified' icon next to the user's name.
   */
  verifiedIcon?: boolean

  /**
   * Displays the Stage TEN logo and sets the color of the name to our brand orange color.
   */
  stageTen?: boolean

  /**
   * Whether to display a colon after the name
   * This colon has special styling applied, so use this option instead of manually inserting a colon
   */
  colon?: boolean

  /**
   * The name to display.
   */
  children: ReactNode
}

/**
 * A name shown within a ChatMessage. Use this component within ChatMessage's children.
 * See ChatMessage.stories.tsx for examples.
 */
export const ChatName = ({
  verifiedIcon,
  stageTen,
  colon,
  children,
}: ChatNameProps) => {
  return (
    <p className="chat-message-text-name" data-stage-ten={stageTen}>
      {stageTen && (
        <StageTen data-size="xs" className="chat-message-stage-ten-icon" />
      )}
      {children}
      {verifiedIcon && (
        <CheckVerifiedSolid
          data-size="xs"
          className="chat-message-verified-icon"
        />
      )}
      {colon && (
        <span className="chat-message-text-name-colon">{": "}</span>
      )}
    </p>
  )
}

export type ChatTextProps = {
  /**
   * The text to display.
   * (it needs to be a string for `linkify` to work properly)
   */
  text: string

  linkify?: boolean
}

/**
 * Text shown within a ChatMessage. Use this component within ChatMessage's children.
 * See ChatMessage.stories.tsx for examples.
 */
export const ChatText = ({ text, linkify }: ChatTextProps) => {
  return linkify ? <Linkify>{text}</Linkify> : text
}

export const chatMessageVariants = [
  "text",
  "rose",
  "overlay-primary",
  "overlay-secondary",
] as const

export type ChatMessageProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Indicates which variant of the chat message is being used. A variant represents
   * essentially different ways of styling the chat message.
   *
   * @default "text"
   */
  variant?: (typeof chatMessageVariants)[number]

  /**
   * An icon to be shown in the inner left of the message
   */
  icon?: keyof typeof AllIcons

  /**
   * children should contain only ChatNames and ChatTexts, which makes up the content of the message.
   * See ChatMessage.stories.tsx for examples.
   */
  children: ReactNode
}

export const ChatMessage = forwardRef<HTMLDivElement, ChatMessageProps>(
  ({ variant = "text", icon, children, className, ...props }, ref) => {
    const Icon = icon ? AllIcons[icon] : null
    return (
      <div
        className={`chat-message ${className}`}
        data-variant={variant}
        data-icon={!!icon}
        ref={ref}
        {...props}
      >
        {icon && (
          <div className="chat-message-icon-container">
            <Icon data-size="xs" />
          </div>
        )}
        <div className="chat-message-text body-md">{children}</div>
      </div>
    )
  },
)
